import { Content } from 'antd/es/layout/layout';
import { Link, useNavigate } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { Breadcrumb, Button, Card, Steps } from 'antd';

import { UploadFile } from 'antd/es/upload/interface';
import { PageHeader } from '@ant-design/pro-layout';
import { Step } from '../../../../types';
import TemplateForm from '../../../Common/TemplateForm';
import modalConfirm from '../../../Common/ModalConfirm';
import TableTemplates from '../../Templates/Table';
import UploadReportPictures from '../../../Common/UploadReportPictures';
import { useContextQcReportUpdateTemplate } from '../../../../context/qcReportUpdateTemplate';
import { TemplateFieldValue, useReportTemplateInWork } from '../../../../hooks/qcReports';
import GalleryModal from '../GalleryModal';
import { useTemplateFieldCount } from '../../../../hooks/templates';

const UpdateTemplate: React.FC = () => {
  const navigate = useNavigate();
  const {
    onSave,
    values,
    setValue,
    isDisabled,
    qcReportsId,
    setIsDisabled,
    reportTemplateGetId,
    reportTemplateUpdate,
    setIsRouteToHome,
  } = useContextQcReportUpdateTemplate();
  const templateFieldCount = useTemplateFieldCount();
  const [countCheck, setCountCheck] = useState<boolean>(false);
  const [disable, setDisable] = useState<boolean>(true);
  const [images, setImages] = useState<any[]>([]);
  const [modalVisibility, setModalVisibility] = useState<boolean>(false);
  const [selectToDelete, setSelectToDelete] = useState(false);
  const [selectedImages, setSelectedImages] = useState<any[]>([]);
  const [saveDeletedImages, SetSaveDeletedImages] = useState(false);

  const handleVisibility = () => {
    setModalVisibility(!modalVisibility);
  };

  const onUploadFilesChange = (uploadFiles: UploadFile[], removeImageId?: string) => {
    if (uploadFiles?.some((el) => el.status === 'uploading') || uploadFiles?.some((el: any) => el?.entity)) {
      setImages((prevState) => ([
        ...prevState,
        ...uploadFiles
          .filter((el) => !prevState.some(({ uid }) => uid.toString() === el.uid.toString())),
      ]));
    }

    if (uploadFiles?.some((el) => el.status === 'done')) {
      if (images.length) {
        setImages((prevState) => ([
          ...prevState.map((obj) => uploadFiles.find((el) => el.uid === obj.uid) || obj),
        ]));
      } else {
        setImages(uploadFiles);
      }
    }

    if (removeImageId?.length) {
      setValue({
        galleryImages: values.galleryImages.filter(({ id }) => id !== +removeImageId),
        oldImages: values.oldImages.filter(({ id }) => id !== +removeImageId),
      });

      setImages(images.filter((el) => el.uid !== removeImageId));
    }
  };

  useEffect(() => {
    if (values.template) {
      templateFieldCount.fetch(undefined, values.template);
    }
  }, [values.template]);

  useEffect(() => {
    if (templateFieldCount.data && templateFieldCount.data > 0) {
      setCountCheck(true);
    } else {
      setCountCheck(false);
    }
  }, [templateFieldCount.data]);

  useEffect(() => {
    if (values.images.length !== 0) {
      setDisable(false);
    } else {
      setDisable(true);
    }
  }, [values.images]);

  useEffect(() => {
    setValue({ images });
  }, [images]);

  const reportTemplateInWork = useReportTemplateInWork();

  const inWork = () => {
    reportTemplateInWork.fetch(undefined, `${reportTemplateGetId?.data?.id}/in-work`);
  };

  useEffect(() => {
    if (reportTemplateGetId?.data?.id) {
      inWork();
      const id = setInterval(inWork, 50000);

      return () => clearInterval(id);
    }

    return undefined;
  }, [reportTemplateGetId?.data]);

  const [currentStep, setCurrentStep] = useState(1);

  const routes = [
    {
      path: '/reports/qc',
      breadcrumbName: 'QC Reports',
    },
    {
      path: `/reports/qc/${values.report}`,
      breadcrumbName: qcReportsId?.data?.record.bolAwb
        ? `${qcReportsId?.data?.record.type.toUpperCase()} ${qcReportsId?.data?.record.bolAwb}` : 'Report details',
    },
    {
      breadcrumbName: 'Add New QC Report Template',
    },
  ];

  const next = () => {
    if (currentStep === 0) {
      if (reportTemplateGetId?.data?.template.id !== values.template) {
        modalConfirm({
          onOk: () => {
            setValue({ reportTemplateFields: [] });
            setCurrentStep(currentStep + 1);
          },
          onCancel: () => setValue({ template: reportTemplateGetId?.data?.template.id || 0 }),
          title: 'Are you sure you want to change the template?',
          content: 'All form filling data will be lost and cannot be restored.',
        });
      } else {
        setValue({
          reportTemplateFields: reportTemplateGetId?.data.reportTemplateFields.map((field) => (
            { id: field.templateField.id, value: field.value }
          )),
        });
        setCurrentStep(currentStep + 1);
      }
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const prev = () => {
    setCurrentStep(currentStep - 1);
  };

  const SelectToDelete = () => {
    setIsDisabled(false);
    setSelectToDelete(true);
  };

  const CancelDelete = () => {
    setIsDisabled(true);
    setSelectToDelete(false);
  };

  const DeleteSelectedImage = () => {
    setSelectToDelete(false);
    if (selectedImages.length > 0) {
      const newFileList = images.filter((file) => !selectedImages.includes(file));

      setImages(newFileList);
      setSelectedImages([]);
      const newOldImage = values.oldImages.filter(({ id }) => !selectedImages.some((s) => s.id === id));
      const newGalleryImages = values.galleryImages.filter(({ id }) => !selectedImages.some((s) => s.id === id));

      setValue({
        galleryImages: newGalleryImages,
        oldImages: newOldImage,
        images: newFileList,
      });
      setIsRouteToHome(false);
      SetSaveDeletedImages(true);
    }
  };

  useEffect(() => {
    if (!selectToDelete && saveDeletedImages) {
      onSave();
      SetSaveDeletedImages(false);
    }
  }, [selectToDelete, saveDeletedImages]);

  const steps: Step[] = [
    {
      title: 'Template',
      content: (
        <TableTemplates
          params={{ status: 'true' }}
          createButton={false}
          selectedRows={values.template ? [values.template] : undefined}
          onRowSelection={(templates) => setValue({ template: templates[0] || 0 })}
        />
      ),
      isValid: !!values.template,
    },
    {
      title: 'Photos',
      content: (
        <Card
          className="edit-qc-image"
          title="Add Photos"
          extra={(
            <div>
              {selectToDelete === false && (
                <Button type="primary" onClick={SelectToDelete}>
                  Select to Delete
                </Button>
              )}
              {selectToDelete === true && (
                <Button type="primary" onClick={CancelDelete}>
                  Cancel
                </Button>
              )}
              {selectToDelete === true && (
                <Button type="primary" onClick={DeleteSelectedImage} style={{ marginLeft: '10px' }}>
                  Delete
                </Button>
              )}
              <Button type="primary" onClick={handleVisibility} style={{ marginLeft: '10px' }}>
                Upload from Gallery
              </Button>
            </div>
          )}
        >
          <UploadReportPictures
            galleryImages={values.galleryImages}
            images={images}
            onUploadFilesChange={onUploadFilesChange}
            oldImages={values.oldImages}
            selectedImage={selectedImages}
            setSelectedImages={setSelectedImages}
            selectToDelete={selectToDelete}
          />
        </Card>
      ),
      isValid: !!(values.images.length || !!values.oldImages.length || !!values.galleryImages.length),
    },
    {
      title: 'Form',
      content: (
        <TemplateForm
          id={values.template}
          onSave={onSave}
          setIsDisabled={(disabled) => setIsDisabled(disabled)}
          setTemplateFields={(reportTemplateFields: TemplateFieldValue[]) => setValue({ reportTemplateFields })}
          templateFieldsValues={values.reportTemplateFields}
        />
      ),
      isValid: !!values.reportTemplateFields,
    },
  ];

  if (countCheck === false) {
    steps.pop();
  }

  return (
    <>
      <PageHeader
        title="Edit QC Report Template"
        extra={(
          <>
            {currentStep > 0 && (
              <Button type="default" onClick={prev}>
                Back
              </Button>
            )}
            {currentStep >= 0 && currentStep !== steps.length - 1 && (
            <Button type="primary" onClick={next} disabled={!steps[currentStep].isValid}>
              Next Step
            </Button>
            )}
            {currentStep === steps.length - 1 && (
              setIsDisabled(disable),
                <Button
                  type="primary"
                  disabled={isDisabled}
                  onClick={onSave}
                  loading={reportTemplateUpdate?.loading}
                >
                  Save
                </Button>
            )}
          </>
        )}
        onBack={() => navigate(-1)}
        breadcrumb={(
          <Breadcrumb>
            {routes.map(({ path, breadcrumbName }) => (
              <Breadcrumb.Item key={breadcrumbName}>
                {path ? <Link to={path}>{breadcrumbName}</Link> : breadcrumbName}
              </Breadcrumb.Item>
            ))}
          </Breadcrumb>
        )}
        footer={(
          <Steps current={currentStep} style={{ maxWidth: '880px' }}>
            {steps.map((item, index) => (
              values.reportTemplateFields.length !== 0 || !countCheck ? (
                <Steps.Step
                  key={item.title}
                  title={item.title}
                  status={index === currentStep ? 'process' : 'finish'}
                />
              ) : (
                <Steps.Step
                  key={item.title}
                  title={item.title}
                />
              )
            ))}
          </Steps>
        )}
      />
      <Content>
        <GalleryModal
          visible={modalVisibility}
          title="QC gallery"
          handleVisibility={handleVisibility}
          setValue={setValue}
          checkedImages={values.galleryImages}
        />
        {steps[currentStep].content}
      </Content>
    </>
  );
};

export default UpdateTemplate;
